import { useState } from 'react';
import type { Category } from '@equinox/shared';
import { useI18n } from '../../i18n/I18nContext.js';
import { categoriesApi } from '../../api/resources.js';
import type { CreateCategoryRequestWithImage, UpdateCategoryRequestWithImage } from '../../api/types.js';
import { ApiError } from '../../lib/apiClient.js';
import { Modal } from '../../components/Modal.js';
import { ImageUploadField } from '../products/ImageUploadField.js';
import { useEscToClose } from '../products/useEscToClose.js';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

export interface CategoryFormModalProps {
  category: Category | null;
  onClose(): void;
  onSaved(category: Category): void;
}

export function CategoryFormModal({ category, onClose, onSaved }: CategoryFormModalProps) {
  const { t } = useI18n();
  const [name, setName] = useState(category?.name ?? '');
  const [color, setColor] = useState(category?.color ?? '#c85630');
  const [imageUrl, setImageUrl] = useState(category?.imageUrl ?? '');
  const [sortOrder, setSortOrder] = useState(category?.sortOrder ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscToClose(onClose, saving);

  const validColor = HEX_RE.test(color);
  const canSave = Boolean(name.trim()) && validColor && !saving;

  async function submit() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      // Built as a named variable of the wider `...WithImage` type, not an
      // inline object literal: `categoriesApi.update`/`create` are typed
      // against the narrower `UpdateCategoryRequest`/`CreateCategoryRequest`
      // (owned elsewhere, not edited here — see api/types.ts's stock & media
      // section on why `imageUrl` lives on a new interface instead of those).
      // A variable of a structurally wider type is assignable to the
      // narrower one without tripping TypeScript's excess-property check,
      // which only fires on literals written directly in call position.
      let saved: Category;
      if (category) {
        const payload: UpdateCategoryRequestWithImage = {
          name: name.trim(),
          color,
          sortOrder,
          imageUrl: imageUrl.trim() || null,
        };
        saved = await categoriesApi.update(category.id, payload);
      } else {
        const payload: CreateCategoryRequestWithImage = {
          name: name.trim(),
          color,
          sortOrder,
          imageUrl: imageUrl.trim() || null,
        };
        saved = await categoriesApi.create(payload);
      }
      onSaved(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.error'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={category ? t('common.edit') : t('categories.new')} onClose={onClose}>
      {error && <p className="field-error">{error}</p>}
      <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-start' }}>
        <div style={{ maxWidth: 96 }}>
          <ImageUploadField
            label={name}
            color={validColor ? color : '#999'}
            imageUrl={imageUrl}
            onImageUrlChange={setImageUrl}
            disabled={saving}
          />
          <p className="field-hint" style={{ textAlign: 'center' }}>
            {t('categories.preview')}
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', flex: 1 }}>
          <label htmlFor="category-name">
            {t('categories.name')}
            <input id="category-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label htmlFor="category-color">
            {t('categories.color')}
            <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
              <input
                id="category-color"
                type="color"
                value={validColor ? color : '#c85630'}
                onChange={(e) => setColor(e.target.value)}
              />
              <input
                aria-label={t('categories.color')}
                value={color}
                onChange={(e) => setColor(e.target.value)}
                style={{ width: 110 }}
              />
            </div>
          </label>
          <label htmlFor="category-sort">
            {t('categories.sortOrder')}
            <input
              id="category-sort"
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
            />
          </label>
        </div>
      </div>

      <div className="modal__actions">
        <button type="button" className="btn btn--secondary" onClick={onClose} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button type="button" className="btn btn--primary" disabled={!canSave} onClick={() => void submit()}>
          {t('categories.save')}
        </button>
      </div>
    </Modal>
  );
}
